import { Action, UUID, initialPageableQuery } from '../app/basicModels';
import { ofType } from 'redux-observable';
import { map, concatMap, catchError, withLatestFrom, flatMap } from 'rxjs/operators';
import { of, Observable, concat } from 'rxjs';
import actions from './actions';
import { getIndicator, saveIndicator, deleteIndicator, getIndicators, getIndicatorsForCompany } from './service';
import { fixIndicatorPayload, getIndicatorDeep, updateIndicatorInPhenomenonsTree } from './utils';
import { IndicatorPage, Indicator, LoadIndicatorQueryPayload } from './models';
import { notification } from '../../components';
import phenomenonActions from '../phenomenon/actions';
import methodologyActions from '../methodology/actions';
import methodologyDeepActions from '../methodologyDeep/actions';
import { injectMetadata } from '../request-control';
import {
  updateIndicatorOnMethodologyDeep,
  addComponentToPhenomenonDeep,
  getCharacteristicsIdsByMethodologyDeep,
  isWrongIndicator,
  removeByIndicatorId,
  removeIndicatorFromMethodologyDeep
} from '../methodologyDeep/services';
import { MethodologyDeep, IndicatorDeep } from '../methodologyDeep/models';
import { applyWorkspaceIdToPayload } from '../methodologyDeep/scope';
import { getComponentDeepWithProperties } from '../methodologyDeep/utils';
import { cloneDeep } from 'lodash';
import { commandRequest } from '../../helpers/request/utils';
import { ErrorMsg, notifyErrorOrFallback } from '../../settings/models/Error';
import { AppState } from '../redux.model';
import type { User } from '../user/models';

type SaveIndicatorEpicAction = ReturnType<typeof actions.saveIndicator> & { metadata?: Record<string, unknown> };

interface SaveIndicatorEpicState {
  methodologyDeep: MethodologyDeep;
  currentUser: User;
  migratedWrites: string;
}

export const handleLoadIndicators = (action$, state$) =>
  action$.pipe(
    ofType(actions.LOAD_INDICATORS),
    map((action: Action<LoadIndicatorQueryPayload>) => action.payload),
    concatMap((query: LoadIndicatorQueryPayload) =>
      getIndicators(query.pageableQuery, query.companyId, query.showCanonicals).pipe(
        map(response => response.data),
        map((indicators: IndicatorPage) => {
          return actions.loadIndicatorsSuccess(indicators);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading indicators!');
          return of(actions.loadIndicatorsFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadIndicatorsForCompany = (action$, state$) =>
  action$.pipe(
    ofType(actions.LOAD_INDICATORS_FOR_COMPANY),
    map((action: Action<any>) => action.payload),
    concatMap((query: any) =>
      getIndicatorsForCompany(query).pipe(
        map(response => response.data),
        map((indicators: IndicatorPage) => {
          return actions.loadIndicatorsForCompanySuccess(indicators.content);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading indicators for company!');
          return of(actions.loadIndicatorsForCompanyFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadIndicator = action$ =>
  action$.pipe(
    ofType(actions.LOAD_INDICATOR),
    map((action: Action<UUID>) => action.payload),
    concatMap((indicatorId: UUID) =>
      getIndicator(indicatorId).pipe(
        map(response => response.data),
        map((indicator: Indicator) => {
          return actions.loadIndicatorSuccess(indicator);
        }),
        catchError(error => {
          let status;
          if (error.message.length > 0) {
            notification('error', error.message, error.status);
          } else {
            const { response } = error;
            const msg = (response && response.data && response.data.detail) || '';
            status = response && response.data && response.data.status;
            notification('error', 'Error loading indicator!', msg);
          }
          return of(actions.loadIndicatorFailure(status || error));
        })
      )
    )
  );

const retrieveShowOnInspectionLayout = (methodologyDeepInspectionGroups, indicatorId) => {
  let resp = false;
  methodologyDeepInspectionGroups.map(group => {
    group.categories.map(category => {
      category.phenomenons.map(phenomenon => {
        phenomenon.components.map(component => {
          if (component.indicator != undefined) {
            if (component.indicator.indicatorId == indicatorId) {
              resp = component.indicator.showOnInspectionLayout;
            }
          }
        });
      });
    });
  });
  return resp;
};

const updateMethodologyDeepForIndicator = (
  indicator: Indicator,
  indicatorId: UUID,
  methodologyDeep: MethodologyDeep,
  orderedComponentId?: UUID,
  orderedPhenomenonId?: UUID
): MethodologyDeep => {
  const inspectionLayoutShow = retrieveShowOnInspectionLayout(methodologyDeep.inspectionLayout.inspectionGroups, indicatorId);
  const characteristicsOnMethodology = getCharacteristicsIdsByMethodologyDeep(methodologyDeep);
  const isWrong = isWrongIndicator(indicator, characteristicsOnMethodology);
  const indicatorDeep: IndicatorDeep = getIndicatorDeep(indicator, orderedComponentId, isWrong, inspectionLayoutShow);
  let updatedMethodology = cloneDeep(methodologyDeep);

  if (!indicator.isNew) {
    updatedMethodology = removeIndicatorFromMethodologyDeep(updatedMethodology, indicatorId);
  }

  if (isWrong) {
    const wrongIndicators = updatedMethodology.wrongIndicators || [];
    if (!wrongIndicators.some(wrongIndicator => wrongIndicator.indicatorId === indicatorId)) {
      updatedMethodology.wrongIndicators = [...wrongIndicators, indicatorDeep];
    }
    return updatedMethodology;
  }

  updatedMethodology.wrongIndicators = removeByIndicatorId(updatedMethodology.wrongIndicators, indicatorId);
  if (orderedPhenomenonId) {
    return addComponentToPhenomenonDeep(updatedMethodology, orderedPhenomenonId, getComponentDeepWithProperties(indicatorDeep));
  }

  return updateIndicatorOnMethodologyDeep(updatedMethodology, indicatorDeep);
};

export const handleSaveIndicator = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_INDICATOR),
    withLatestFrom(
      state$.pipe(
        map((state: AppState) => ({
          methodologyDeep: state.MethodologyDeep.methodologyDeep as MethodologyDeep,
          currentUser: state.User.currentUser as User,
          migratedWrites: (state.App?.systemFlags?.P40_55336_MIGRATED_CONFIGURATION_WRITES ?? '[]') as string
        }))
      )
    ),
    concatMap(([action, state]: [SaveIndicatorEpicAction, SaveIndicatorEpicState]) => {
      const { methodologyDeep, currentUser, migratedWrites } = state;
      const { indicator } = action.payload;
      const { orderedComponentId, orderedPhenomenonId, methodologyId } = action.payload;
      const indicatorPayload = fixIndicatorPayload(indicator, methodologyDeep?.workspace_id);
      const request = commandRequest<Indicator>(indicatorPayload, currentUser);
      request.payload = applyWorkspaceIdToPayload(request.payload, methodologyDeep?.workspace_id);
      const defaultExpression = Boolean(request.payload.default);
      const saveObservable = saveIndicator(request, request.payload.isNew, defaultExpression, migratedWrites);

      return saveObservable.pipe(
        concatMap(response => {
          const saveIndicatorSuccess = injectMetadata(actions.saveIndicatorSuccess, { ...action.metadata, response });
          if (methodologyId && request.payload.id) {
            const indicatorId = request.payload.id;
            const methodology = updateMethodologyDeepForIndicator(
              request.payload,
              indicatorId,
              methodologyDeep,
              orderedComponentId,
              orderedPhenomenonId
            );
            notification('success', 'Indicator saved!');
            const actionsSaveIndicatorAndUpdateMethodologyDeep = [
              saveIndicatorSuccess(request.payload),
              methodologyDeepActions.updateMethodologyDeep(methodology)
            ];
            if (action.payload.orderedPhenomenonId) {
              actionsSaveIndicatorAndUpdateMethodologyDeep.push(methodologyDeepActions.toggleAddCharacteristicIndicatorModal());
            }
            return concat(actionsSaveIndicatorAndUpdateMethodologyDeep);
          }
          notification('success', 'Indicator saved!');
          return of(saveIndicatorSuccess({ ...request.payload, isNew: false }));
        }),
        catchError((error: any) => {
          const msg = error.response && error.response.data ? error.response.data.message : '';
          const saveIndicatorFailure = injectMetadata(actions.saveIndicatorFailure, {
            ...action.metadata,
            response: error.response
          });

          return of(saveIndicatorFailure(msg || 'Error saving indicator!'));
        })
      );
    })
  );

export const handleDeleteIndicator = (action$, state$) =>
  action$.pipe(
    ofType(actions.DELETE_INDICATOR),
    map((action: Action<Indicator>) => action.payload),
    withLatestFrom(
      state$.pipe(map((state: any) => state.Methodology.methodologySelect)),
      state$.pipe(map((state: any) => state.Phenomenon.phenomenonsTree)),
      state$.pipe(
        map((state: any) => {
          return state.User.currentUser;
        })
      ),
      state$.pipe(map((state: AppState) => state.Phenomenon.phenomenonSelect))
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map(([indicator, methodology, phenomenonsTree, currentUser, phenomenonSelect]: any) => {
      const mutablePhenomenonsTree = phenomenonsTree;
      let phenomenonIndex = -1;
      let indicatorIndex = -1;
      mutablePhenomenonsTree.forEach((phenomenon, index) => {
        phenomenon.indicators.forEach((indicator_obj, indicator_index) => {
          if (indicator_obj.id === indicator.id) {
            phenomenonIndex = index;
            indicatorIndex = indicator_index;
          }
        });
      });
      if (phenomenonIndex !== -1) {
        const mutableIndicators = mutablePhenomenonsTree[phenomenonIndex].indicators;
        mutableIndicators.splice(indicatorIndex, 1);
        mutablePhenomenonsTree[phenomenonIndex].indicators = mutableIndicators;
      }
      const updatedPhenomenonSelect =
        phenomenonSelect && phenomenonSelect.id === indicator.phenomenon_id
          ? {
              ...phenomenonSelect,
              indicators_dto: (phenomenonSelect.indicators_dto || []).filter(i => i.id !== indicator.id)
            }
          : null;
      const indicatorToDelete = {
        account: {
          id: currentUser.id,
          name: currentUser.name
        },
        payload: {
          ...indicator
        }
      };
      return [methodology, indicatorToDelete, updatedPhenomenonSelect];
    }),
    withLatestFrom(state$.pipe(map((state: AppState) => state.App?.systemFlags?.P40_55336_MIGRATED_CONFIGURATION_WRITES ?? '[]'))),
    concatMap(([[methodologySelect, indicatorToDelete, updatedPhenomenonSelect], migratedWrites]) =>
      deleteIndicator(indicatorToDelete, migratedWrites).pipe(
        flatMap(() => {
          notification('success', 'Indicator deleted!');
          const refreshSelectAction = updatedPhenomenonSelect ? [phenomenonActions.selectPhenomenon(updatedPhenomenonSelect)] : [];
          if (indicatorToDelete.payload.company_id) {
            return concat([
              phenomenonActions.loadPhenomenonsTree({ ...initialPageableQuery }, indicatorToDelete.payload.company_id),
              methodologyActions.updateMethodologyDraft(methodologySelect),
              actions.deleteIndicatorSuccess(indicatorToDelete.payload)
            ]);
          }
          return concat([...refreshSelectAction, actions.deleteIndicatorSuccess(indicatorToDelete.payload)]);
        }),
        catchError((error: any) => {
          const msg = notifyErrorOrFallback(error, 'Error deleting indicator!');
          return of(actions.deleteIndicatorFailure(msg));
        })
      )
    )
  );

export const handleSaveIndicatorSuccess = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_INDICATOR_SUCCESS),
    map((action: Action<Indicator>) => action.payload),
    concatMap((indicator: Indicator): Observable<any> => {
      const { phenomenonsTree } = state$.value.Phenomenon;
      const newPhenomenonsTree = updateIndicatorInPhenomenonsTree(indicator, phenomenonsTree);
      return of(phenomenonActions.loadPhenomenonsTreeSuccess(newPhenomenonsTree));
    })
  );
